import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import dataQualityClient from "Clients/data-quality-client.js";
import { FleetOverviewType } from "@/types/dataQuality/fleetOverview";
import { DataAvailabilityConfiguration, DataAvailabilityType, DataAvailabilityConfig } from "@/types/dataQuality/dataAvailability";
import { DataOutliersTakeActionType, DataOutliersType, DataOutliersViewHistoryType } from "@/types/dataQuality/dataOutliers";
import { SpeedLogSensorQuality } from "@/types/dataQuality/SpeedLogSensorQuality";
import { SpeedThroughWater } from "@/types/dataQuality/SpeedThroughWater";
import { DataOutlier, FeatureConfig } from "@/types/dataQuality/dataOutliers";
import { DataQualityDataAlignmentModel } from "@/types/dataQuality/dataAlignment";

@Module({ namespaced: true, name: "DataQuality" })
class DataQuality extends VuexModule {
  private _loadingState = false;
  private _modalLoadingState = false;
  private _speedLogDataLoadingState = false;
  private _derivedSpeedThroughWaterState = false;
  private _speedThroughWaterState = false;
  private _dataAlignmentLoadingState = false;
  private _featureValues: any[] = [];
  private _fleetOverview: FleetOverviewType[] = [];
  private _dataAvailability: DataAvailabilityType[] = [];
  private _dataAvailabilityConfig: DataAvailabilityConfig | null = null;
  private _dataOutliers: DataOutliersType[] = [];
  private _companyOutliers: DataOutliersType[] = [];
  private _dataOutliersTakeAction: DataOutliersTakeActionType[] = [];
  private _dataOutliersViewHistory: DataOutliersViewHistoryType[] = [];
  private _speedLogData: SpeedLogSensorQuality = {} as SpeedLogSensorQuality;
  private _derivedSpeedThroughWaterData: SpeedThroughWater = {} as SpeedThroughWater;
  private _speedThroughWaterData: SpeedThroughWater = {} as SpeedThroughWater;
  private _dataAlignmentData: DataQualityDataAlignmentModel = {} as DataQualityDataAlignmentModel;
  private _features: any[] = [];
  private _availabilityFeatures: any[] = [];
  private _outlierFeatures: any[] = [];

  @Mutation
  public SET_LOADING_STATE(loading: boolean): void {
    this._loadingState = loading;
  }

  @Mutation
  public SET_MODAL_LOADING_STATE(loading: boolean): void {
    this._modalLoadingState = loading;
  }

  @Mutation
  public SET_FEATURE_VALUES(featureValues: any[]): void {
    this._featureValues = featureValues;
  }

  @Mutation
  public SET_SPEED_LOG_DATA_LOADING_STATE(loading: boolean): void {
    this._speedLogDataLoadingState = loading;
  }

  @Mutation
  public SET_DERIVED_SPEED_THROUGH_WATER_LOADING_STATE(loading: boolean): void {
    this._derivedSpeedThroughWaterState = loading;
  }

  @Mutation
  public SET_SPEED_THROUGH_WATER_LOADING_STATE(loading: boolean): void {
    this._speedThroughWaterState = loading;
  }

  @Mutation
  public REFRESH_FLEET_OVERVIEW(fleetOverview: FleetOverviewType[]): void {
    this._fleetOverview = fleetOverview;
  }

  @Mutation
  public REFRESH_DATA_AVAILABILITY(dataAvailability: DataAvailabilityType[]): void {
    this._dataAvailability = dataAvailability;
  }

  @Mutation
  public REFRESH_DATA_AVAILABILITY_CONFIG(dataAvailabilityConfig: DataAvailabilityConfig): void {
    this._dataAvailabilityConfig = dataAvailabilityConfig;
  }

  @Mutation
  public REFRESH_DATA_OUTLIERS(dataOutliers: DataOutliersType[]): void {
    this._dataOutliers = dataOutliers;
  }

  @Mutation
  public SET_COMPANY_OUTLIERS(companyOutliers: DataOutliersType[]): void {
    this._companyOutliers = companyOutliers;
  }

  @Mutation
  public REFRESH_DATA_OUTLIERS_TAKE_ACTION(dataOutliersTakeAction: DataOutliersTakeActionType[]): void {
    this._dataOutliersTakeAction = dataOutliersTakeAction;
  }
  @Mutation
  public REFRESH_DATA_OUTLIERS_VIEW_HISTORY(dataOutliersViewHistory: DataOutliersViewHistoryType[]): void {
    this._dataOutliersViewHistory = dataOutliersViewHistory;
  }

  @Mutation
  public SET_SPEED_LOG_DATA(data: SpeedLogSensorQuality): void {
    this._speedLogData = data;
  }

  @Mutation
  public SET_DERIVED_SPEED_THROUGH_WATER_DATA(data: SpeedThroughWater): void {
    this._derivedSpeedThroughWaterData = data;
  }

  @Mutation
  public SET_SPEED_THROUGH_WATER_DATA(data: SpeedThroughWater): void {
    this._speedThroughWaterData = data;
  }

  @Mutation
  public SET_DATA_ALIGNMENT_DATA(data: DataQualityDataAlignmentModel): void {
    this._dataAlignmentData = data;
  }


  @Mutation
  public SET_DATA_ALIGNMENT_DATA_LOADING_STATE(loading: boolean): void {
    this._dataAlignmentLoadingState = loading;
  }

  @Mutation
  public SET_FEATURES(features: any[]): void {
    this._features = features;
  }

  @Mutation
  public SET_AVAILABILITY_FEATURES(features: any[]): void {
    this._availabilityFeatures = features;
  }

  @Mutation
  public SET_OUTLIER_FEATURES(features: any[]): void {
    this._outlierFeatures = features;
  }

  @Action({ rawError: true })
  public async refreshFleetOverview(): Promise<void> {
    let fleetOverview = [];
    try {
      this.context.commit("SET_LOADING_STATE", true);
      fleetOverview = await dataQualityClient.getFleetOverview();
      this.context.commit("REFRESH_FLEET_OVERVIEW", fleetOverview);
      this.context.commit("SET_LOADING_STATE", false);
    } catch (error) {
      throw ({ message: "Failed to refresh fleet overview -> ", error });
    }
  }

  @Action({ rawError: true })
  public async refreshDataAvailability(): Promise<void> {
    let dataAvailability = [];
    try {
      this.context.commit("SET_LOADING_STATE", true);
      dataAvailability = await dataQualityClient.getDataOutage();
      this.context.commit("REFRESH_DATA_AVAILABILITY", dataAvailability);
      this.context.commit("SET_LOADING_STATE", false);
    } catch (error) {
      throw ({ message: "Failed to refresh data availability -> ", error });
    }
  }

  @Action({ rawError: true })
  public async refreshDataAvailabilityConfig(): Promise<void> {
    let dataAvailabilityConfig = [];
    try {
      this.context.commit("SET_MODAL_LOADING_STATE", true);
      dataAvailabilityConfig = await dataQualityClient.getDataOutageConfig();
      this.context.commit("REFRESH_DATA_AVAILABILITY_CONFIG", dataAvailabilityConfig);
      this.context.commit("SET_MODAL_LOADING_STATE", false);
    } catch (error) {
      throw ({ message: "Failed to refresh data availability config -> ", error });
    }
  }

  @Action({ rawError: true })
  public async updateDataAvailabilityConfig(configuration: DataAvailabilityConfiguration[]): Promise<void> {
    try {
      await dataQualityClient.updateDataOutageConfig(configuration);
    } catch (error) {
      throw ({ message: "Failed to update Data Availability Config", error });
    }
  }

  @Action({ rawError: true })
  public async fetchCompanyOutliers(query: { companyId: number | null; fromDate?: string; toDate?: string }): Promise<void> {
    if (query.companyId === null) return;
    try {
      this.context.commit("SET_LOADING_STATE", true);
      const companyOutliers: DataOutliersType[] = await dataQualityClient.getCompanyOutliers(query);
      this.context.commit("SET_COMPANY_OUTLIERS", companyOutliers);
      this.context.commit("SET_LOADING_STATE", false);
    } catch (error) {
      throw ({ message: "Failed to fetch features mapping -> ", error });
    }
  }

  @Action({ rawError: true })
  public async fetchFeatureValues(): Promise<void> {
    try {
      const featureValues = await dataQualityClient.getFeatureValues();
      this.context.commit("SET_FEATURE_VALUES", featureValues);
    } catch (error) {
      throw ({ message: "Failed to fetch feature values -> ", error });
    }
  }

  @Action({ rawError: true })
  public async fetchFeatures(): Promise<void> {
    try {
      const features = await dataQualityClient.getFeatures();
      this.context.commit("SET_FEATURES", features);
    } catch (error) {
      throw ({ message: "Failed to fetch features -> ", error });
    }
  }

  @Action({ rawError: true })
  public async fetchAvailabilityFeatures(companyId: number): Promise<void> {
    try {
      const features = await dataQualityClient.getAvailabilityFeatures({ companyId });
      this.context.commit("SET_AVAILABILITY_FEATURES", features);
    } catch (error) {
      throw ({ message: "Failed to fetch availability features -> ", error });
    }
  }

  @Action({ rawError: true })
  public async saveAvailabilityFeatures({ companyId, featureNames }: { companyId: number; featureNames: string[] }): Promise<void> {
    try {
      await dataQualityClient.setAvailabilityFeatures(companyId, featureNames);
    } catch (error) {
      throw ({ message: "Failed to save availability features -> ", error });
    }
  }

  @Action({ rawError: true })
  public async fetchOutlierFeatures(companyId: number): Promise<void> {
    try {
      const features = await dataQualityClient.getOutlierFeatures({ companyId });
      this.context.commit("SET_OUTLIER_FEATURES", features);
    } catch (error) {
      throw ({ message: "Failed to fetch outlier features -> ", error });
    }
  }

  @Action({ rawError: true })
  public async saveOutlierFeatures({ companyId, featureNames }: { companyId: number; featureNames: string[] }): Promise<void> {
    try {
      await dataQualityClient.setOutlierFeatures(companyId, featureNames);
    } catch (error) {
      throw ({ message: "Failed to save outlier features -> ", error });
    }
  }

  @Action({ rawError: true })
  public async fetchFeatureConfigs(vesselId: string): Promise<FeatureConfig[]> {
    try {
      return await dataQualityClient.getFeatureConfigs({ vesselId: vesselId });
    } catch (error) {
      throw ({ message: "Failed to fetch feature config -> ", error });
    }
  }

  @Action({ rawError: true })
  public async updateFeatureConfigs(config: FeatureConfig[]): Promise<void> {
    try {
      return await dataQualityClient.updateFeatureConfigs(config);
    } catch (error) {
      throw ({ message: "Failed to update feature config -> ", error });
    }
  }

  @Action({ rawError: true })
  public async recalculateOutliers(vesselId: string): Promise<void> {
    try {
      return await dataQualityClient.recalculateOutliers({ vesselId: vesselId });
    } catch (error) {
      throw ({ message: "Failed to recalculate outliers -> ", error });
    }
  }

  @Action({ rawError: true })
  public async updateOutliersAcknowledge(outliers: DataOutliersType[]): Promise<void> {
    try {
      this.context.commit("SET_MODAL_LOADING_STATE", true);
      await dataQualityClient.updateOutliersAcknowledge(outliers);
      this.context.commit("SET_MODAL_LOADING_STATE", false);
    } catch (error) {
      throw ({ message: "Failed to acknowledge Data Outliers", error });
    }
  }

  @Action({ rawError: true })
  public async updateOutliersRemove(outliers: DataOutliersType[]): Promise<void> {
    try {
      this.context.commit("SET_MODAL_LOADING_STATE", true);
      await dataQualityClient.updateOutliersRemove(outliers);
      this.context.commit("SET_MODAL_LOADING_STATE", false);
    } catch (error) {
      throw ({ message: "Failed to remove Data Outliers", error });
    }
  }

  @Action({ rawError: true })
  public async updateOutliersClear(outliers: DataOutliersType[]): Promise<void> {
    try {
      this.context.commit("SET_MODAL_LOADING_STATE", true);
      await dataQualityClient.updateOutliersClear(outliers);
      this.context.commit("SET_MODAL_LOADING_STATE", false);
    } catch (error) {
      throw ({ message: "Failed to clear Data Outliers", error });
    }
  }

  @Action({ rawError: true })
  public async fetchSpeedLog(query: { imo: number; from: string; to: string; }): Promise<void> {
    try {
      this.context.commit("SET_SPEED_LOG_DATA_LOADING_STATE", true);
      const response = await dataQualityClient.getSpeedLog(query);
      this.context.commit("SET_SPEED_LOG_DATA", response);
      this.context.commit("SET_SPEED_LOG_DATA_LOADING_STATE", false);
    } catch (error) {
      this.context.commit("SET_SPEED_LOG_DATA_LOADING_STATE", false);
      throw ({ message: "Failed to fetch speed log data -> ", error });
    }
  }
  @Action({ rawError: true })
  public async fetchDerivedSpeedThroughWater(query: { imoNumber: number; }): Promise<void> {
    try {
      this.context.commit("SET_DERIVED_SPEED_THROUGH_WATER_LOADING_STATE", true);
      const response = await dataQualityClient.getDerivedSpeedThroughWater(query);
      this.context.commit("SET_DERIVED_SPEED_THROUGH_WATER_DATA", response);
      this.context.commit("SET_DERIVED_SPEED_THROUGH_WATER_LOADING_STATE", false);
    } catch (error) {
      this.context.commit("SET_DERIVED_SPEED_THROUGH_WATER_LOADING_STATE", false);
      throw ({ message: "Failed to fetch derived speed through water data -> ", error });
    }
  }

  @Action({ rawError: true })
  public async fetchSpeedThroughWater(query: { imoNumber: number; }): Promise<void> {
    try {
      this.context.commit("SET_SPEED_THROUGH_WATER_LOADING_STATE", true);
      const response = await dataQualityClient.getSpeedThroughWater(query);
      this.context.commit("SET_SPEED_THROUGH_WATER_DATA", response);
      this.context.commit("SET_SPEED_THROUGH_WATER_LOADING_STATE", false);
    } catch (error) {
      this.context.commit("SET_SPEED_THROUGH_WATER_LOADING_STATE", false);
      throw ({ message: "Failed to fetch speed through water data -> ", error });
    }
  }

  @Action({ rawError: true })
  public async fetchDataAlignment(query: { vesselId: number; fromDate: string; toDate: string; }): Promise<void> {
    try {
      this.context.commit("SET_DATA_ALIGNMENT_DATA_LOADING_STATE", true);
      const response = await dataQualityClient.getDataAlignment(query);
      this.context.commit("SET_DATA_ALIGNMENT_DATA", response);
      this.context.commit("SET_DATA_ALIGNMENT_DATA_LOADING_STATE", false);
    } catch (error) {
      this.context.commit("SET_DATA_ALIGNMENT_DATA_LOADING_STATE", false);
      throw ({ message: "Failed to fetch data alignment data -> ", error });
    }
  }


  //  @Getters
  public get fleetOverview(): FleetOverviewType[] {
    return this._fleetOverview;
  }

  public get featureValues(): any[] {
    return this._featureValues;
  }

  public get features(): any[] {
    return this._features;
  }

  public get availabilityFeatures(): any[] {
    return this._availabilityFeatures;
  }

  public get outlierFeatures(): any[] {
    return this._outlierFeatures;
  }

  public get dataAvailability(): DataAvailabilityType[] {
    return this._dataAvailability;
  }

  public get dataAvailabilityConfig(): DataAvailabilityConfig | null {
    return this._dataAvailabilityConfig;
  }

  public get companyOutliers(): DataOutliersType[] {
    return this._companyOutliers;
  }

  public get dataOutliers(): DataOutliersType[] {
    return this._dataOutliers;
  }

  public get dataOutliersTakeAction(): DataOutliersTakeActionType[] {
    return this._dataOutliersTakeAction;
  }

  public get dataOutliersViewHistory(): DataOutliersViewHistoryType[] {
    return this._dataOutliersViewHistory;
  }

  public get loadingState(): boolean {
    return this._loadingState;
  }

  public get modalLoadingState(): boolean {
    return this._modalLoadingState;
  }

  public get speedLogDataLoadingState(): boolean {
    return this._speedLogDataLoadingState;
  }

  public get derivedSpeedThroughWaterLoadingState(): boolean {
    return this._derivedSpeedThroughWaterState;
  }

  public get speedThroughWaterLoadingState(): boolean {
    return this._speedThroughWaterState;
  }

  public get speedLogData(): SpeedLogSensorQuality {
    return this._speedLogData;
  }

  public get derivedSpeedThroughWaterData(): SpeedThroughWater {
    return this._derivedSpeedThroughWaterData;
  }

  public get speedThroughWaterData(): SpeedThroughWater {
    return this._speedThroughWaterData;
  }

  public get dataAlignmentData(): DataQualityDataAlignmentModel {
    return this._dataAlignmentData;
  }

  public get dataAlignmentLoadingState(): boolean {
    return this._dataAlignmentLoadingState;
  }


}

export default DataQuality;
